
/**
 * Main email sending service
 */
import { toast } from "sonner";
import type { EmailFormData } from './emailTemplates';
import { sendEmailViaSupabase } from './strategies/supabaseEmailStrategy';
import { sendEmailViaEmailJS } from './strategies/emailJsStrategy';
import { getEmailErrorInfo } from './emailTypes';

/**
 * Main email sending function that tries Supabase first, then falls back to EmailJS
 */
export const sendEmail = async (formData: EmailFormData): Promise<boolean> => {
  try {
    // Try to send email using Supabase edge function first
    try {
      const result = await sendEmailViaSupabase(formData);
      
      // Show appropriate message based on which emails were sent
      if (result.adminSent && result.customerSent) {
        toast.success("¡Mensaje enviado con éxito!", {
          description: "Nos pondremos en contacto contigo lo antes posible.",
        });
      } else if (result.adminSent) {
        toast.success("¡Mensaje recibido!", {
          description: "Recibimos tu mensaje pero hubo un problema enviando la confirmación a tu correo.",
        });
      } else if (result.customerSent) {
        toast.success("¡Mensaje enviado!", {
          description: "Te hemos enviado una confirmación por correo.",
        });
      }
      
      return true;
    } catch (supabaseError) {
      console.warn('Falling back to direct EmailJS implementation:', supabaseError);
      // Continue to fallback implementation
    }
    
    // Fallback to direct EmailJS implementation
    const result = await sendEmailViaEmailJS(formData);
    
    // Handle results based on which emails were sent
    if (result.adminSent && result.customerSent) {
      console.log('Both emails sent successfully');
      toast.success("¡Mensaje enviado con éxito!", {
        description: "Nos pondremos en contacto contigo lo antes posible.",
      });
    } else if (result.adminSent) {
      console.log('Only admin email sent successfully');
      toast.success("¡Mensaje recibido!", {
        description: "Recibimos tu mensaje pero hubo un problema enviando la confirmación a tu correo.",
      });
    } else if (result.customerSent) {
      console.log('Only customer email sent successfully');
      toast.success("¡Mensaje enviado!", {
        description: "Te hemos enviado una confirmación por correo.",
      });
    }
    
    return true;
  } catch (error) {
    console.error('EmailJS Error:', error);
    
    const errorInfo = getEmailErrorInfo(error);
    
    toast.error(errorInfo.title, {
      description: errorInfo.message,
    });
    
    return false;
  }
};
